import "./App.scss";
import MainContent from "./components/MainContent/MainContent";
import TopBar from "./components/TopBar/TopBar";
import SystemBanners from "./components/SystemBanners/SystemBanners";
import LeftSideBar from "./components/LeftSideBar/LeftSideBar";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import SuperDashboard from "./pages/SuperDashboard/SuperDashboard";
import Dashboard from "./pages/Dashboard/Dashboard";
import Tasks from "./pages/Tasks/Tasks";
import MyDay from "./pages/MyDay/MyDay";
import Notes from "./pages/Notes/Notes";
import PostIts from "./pages/PostIts/PostIts";
import ToDoList from "./pages/ToDoList/ToDoList";
import Demo from "./pages/Demo/Demo";
import Help from "./pages/Help/Help";
import LoginPage from "./pages/LoginFlow/LoginPage";
import AuthLayout from "./layouts/AuthLayout";
import PrivateRoute from "./components/PrivateRoute";
import SignupPage from "./pages/LoginFlow/SignupPage";
import EnterpriseSignupPage from "./pages/LoginFlow/EnterpriseSignupPage";
import VerifyPage from "./pages/LoginFlow/VerifyPage";
import ForgotPasswordPage from "./pages/LoginFlow/ForgotPasswordPage";
import ResetPasswordPage from "./pages/LoginFlow/ResetPasswordPage";
import ChangePasswordPage from "./pages/LoginFlow/ChangePasswordPage";
import AcceptInvitePage from "./pages/LoginFlow/AcceptInvitePage";
import TermsOfService from "./pages/Legal/TermsOfService";
import PrivacyPolicy from "./pages/Legal/PrivacyPolicy";
import DataProcessingAgreement from "./pages/Legal/DataProcessingAgreement";
import { useEffect, useState, useRef } from "react";
import useAuthStore from "./store/authStore";
import useChatStore from "./store/chatStore";
import Modal from "./components/Modal/Modal";
import ChatModal from "./components/ModalContent/ChatModal/ChatModal";
import PostItsOverlay from "./components/PostItsOverlay/PostItsOverlay";
import PinnedPostIts from "./components/PinnedPostIts/PinnedPostIts";
import { getOrganizationMembers } from "./api/organizations/organizations";
import { useSocket } from "./context/SocketContext";
import { refreshAccountLanguage, applyLanguage } from "./i18n/language";
import { refreshAccountTheme, applyTheme } from "./theme/theme";

const authRoutes = [
  "/login",
  "/signup",
  "/forgot-password",
  "/reset-password",
  "/verify",
  "/change-password",
  "/accept-invite",
  "/legal/terms-of-service",
  "/legal/privacy-policy",
  "/legal/data-processing-agreement",
];

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState("default");
  const { token, user, organizations, currentProject } = useAuthStore();
  const { getTotalUnreadCount, fetchConversations, conversations, setSocket } =
    useChatStore();
  const { socket } = useSocket();
  const chatModalRef = useRef(null);
  const postItsRef = useRef(null);
  const [showChatButton, setShowChatButton] = useState(false);
  const [isPostItsOpen, setIsPostItsOpen] = useState(false);

  // Use the language saved on the user's account: on start/login and when
  // the app comes back to the foreground (it may have been changed on
  // another device while this one was closed or asleep)
  const userId = user?._id || user?.id;
  useEffect(() => {
    if (!userId) return;
    refreshAccountLanguage();
    refreshAccountTheme();
    const handleVisible = () => {
      if (document.hidden) return;
      refreshAccountLanguage();
      refreshAccountTheme();
    };
    document.addEventListener("visibilitychange", handleVisible);
    return () =>
      document.removeEventListener("visibilitychange", handleVisible);
  }, [userId]);

  // The language was changed on another device while this one is open (and
  // re-check after a reconnect, in case the event was missed)
  useEffect(() => {
    if (!socket) return;
    const handleLanguage = ({ language }) => applyLanguage(language);
    const handleTheme = ({ theme }) => applyTheme(theme);
    const handleReconnect = () => {
      refreshAccountLanguage();
      refreshAccountTheme();
    };
    socket.on("user:language", handleLanguage);
    socket.on("user:theme", handleTheme);
    socket.on("connect", handleReconnect);
    return () => {
      socket.off("user:language", handleLanguage);
      socket.off("user:theme", handleTheme);
      socket.off("connect", handleReconnect);
    };
  }, [socket]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "default" ? "alternative" : "default"));
  };

  const handlePostItsClick = () => {
    if (postItsRef.current) {
      postItsRef.current.toggle();
    }
  };

  const handleClosePostIts = () => {
    if (postItsRef.current) {
      postItsRef.current.close();
    }
  };

  // Fetch conversations on app startup to get unread counts
  useEffect(() => {
    const loadConversations = async () => {
      if (!token || !user?.currentOrganization) return;

      try {
        await fetchConversations(token);
      } catch (error) {
        console.error("Error fetching conversations:", error);
      }
    };

    loadConversations();
  }, [token, user, fetchConversations]);

  // Set up socket connection for real-time chat updates
  useEffect(() => {
    if (socket) {
      setSocket(socket);
    }
  }, [socket, setSocket]);

  // Check if there are other members in the organization
  useEffect(() => {
    const checkOrgMembers = async () => {
      // Don't run if we don't have necessary data
      if (
        !token ||
        !user?.currentOrganization ||
        !organizations ||
        organizations.length === 0
      ) {
        setShowChatButton(false);
        return;
      }

      // Check if the currentOrganization actually exists in the organizations array
      const currentOrg = organizations.find(
        (org) => org._id === user.currentOrganization,
      );

      // If organization doesn't exist in the array, don't try to fetch members
      if (!currentOrg) {
        setShowChatButton(false);
        return;
      }

      try {
        const members = await getOrganizationMembers(
          user.currentOrganization,
          token,
        );
        // Show chat button only if there are other active members (not including self and pending)
        const activeMembers = (members || []).filter(
          (member) =>
            member.id !== user.id &&
            member.id !== user._id &&
            member.status !== "pending",
        );
        setShowChatButton(activeMembers.length > 0);
      } catch (error) {
        // Silently fail - this is not critical functionality
        setShowChatButton(false);
      }
    };

    checkOrgMembers();
  }, [token, user, organizations]);

  // Update unread count when conversations change (reactive, no polling)
  useEffect(() => {
    if (!showChatButton) return;

    // Compute unread count directly from store state
    getTotalUnreadCount();
  }, [showChatButton, conversations, getTotalUnreadCount]);

  const isAuthRoute = authRoutes.some((path) =>
    location.pathname.startsWith(path),
  );

  // Redirect to login if token is cleared (e.g., due to 401 error)
  useEffect(() => {
    if (!token && !isAuthRoute) {
      navigate("/login");
    }
  }, [token, isAuthRoute, navigate]);

  // Fetch organizations on load
  // useEffect(() => {
  //   const loadOrganizations = async () => {
  //     if (token && user) {
  //       const organizations = await fetchOrganizations();
  //     }
  //   };

  //   loadOrganizations();
  // }, [token, user, fetchOrganizations]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!e.ctrlKey) return;
      switch (e.key) {
        case "1":
          navigate("/dashboard");
          break;
        case "2":
          navigate("/boards");
          break;
        case "3":
          navigate("/notes");
          break;
        case "t":
          toggleTheme();
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [navigate]);

  // Check session validity when tab becomes visible after being hidden
  useEffect(() => {
    const checkAuth = useAuthStore.getState().checkAuth;
    let lastActivityTime = Date.now();
    const STALE_THRESHOLD = 5 * 60 * 1000; // 5 minutes

    const handleVisibilityChange = () => {
      if (!document.hidden && token) {
        const timeSinceLastActivity = Date.now() - lastActivityTime;

        // If tab was hidden for more than threshold, ping server to check session
        if (timeSinceLastActivity > STALE_THRESHOLD) {
          console.log(
            "Tab visible after being hidden - checking session validity",
          );
          checkAuth();
        }

        lastActivityTime = Date.now();
      }
    };

    // Update last activity time on any user interaction
    const updateActivity = () => {
      lastActivityTime = Date.now();
    };

    // Listen for visibility changes
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Track user activity
    window.addEventListener("mousedown", updateActivity);
    window.addEventListener("keydown", updateActivity);
    window.addEventListener("touchstart", updateActivity);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("mousedown", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("touchstart", updateActivity);
    };
  }, [token]);

  // Role-based wrappers
  const RoleProtectedRoute = ({ children, allowedRoles }) => {
    const user = useAuthStore((state) => state.user);
    if (!user || !allowedRoles.includes(user.role)) {
      return <Navigate to="/boards" replace />;
    }
    return children;
  };

  const OrgAdminProtectedRoute = ({ children }) => {
    const { user, organizations } = useAuthStore();
    const currentOrganization = organizations.find(
      (org) => org._id === user?.currentOrganization,
    );
    const userId = user?._id || user?.id;
    const isOrgAdmin =
      currentOrganization?.members.find(
        (m) => String(m.userId) === String(userId),
      )?.role === "admin";

    if (!isOrgAdmin) {
      return <Navigate to="/boards" replace />;
    }
    return children;
  };

  return (
    <div className={`app theme-${theme}`}>
      {isAuthRoute ? (
        <AuthLayout>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route
              path="/signup-enterprise"
              element={<EnterpriseSignupPage />}
            />
            <Route path="/verify/:token" element={<VerifyPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/reset-password/:token"
              element={<ResetPasswordPage />}
            />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            <Route
              path="/accept-invite/:token"
              element={<AcceptInvitePage />}
            />
            <Route
              path="/legal/terms-of-service"
              element={<TermsOfService />}
            />
            <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/legal/data-processing-agreement"
              element={<DataProcessingAgreement />}
            />
          </Routes>
        </AuthLayout>
      ) : (
        <>
          <TopBar onClosePostIts={handleClosePostIts} />
          <SystemBanners />
          <div className="main-wrapper">
            <LeftSideBar
              onClosePostIts={handleClosePostIts}
              onTogglePostIts={handlePostItsClick}
              isPostItsOpen={isPostItsOpen}
            />
            <MainContent>
              <Routes>
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <Tasks />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <OrgAdminProtectedRoute>
                        <Dashboard />
                      </OrgAdminProtectedRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/my-day"
                  element={
                    <PrivateRoute>
                      <MyDay />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/boards"
                  element={
                    <PrivateRoute>
                      <Tasks />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/notes"
                  element={
                    <PrivateRoute>
                      <Notes />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/todolists"
                  element={
                    <PrivateRoute>
                      <ToDoList />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/project-lists"
                  element={
                    <PrivateRoute>
                      <ToDoList
                        scope="project"
                        projectId={currentProject?._id}
                      />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/personal-lists"
                  element={
                    <PrivateRoute>
                      <ToDoList scope="personal" />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/help"
                  element={
                    <PrivateRoute>
                      <Help />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/demo"
                  element={
                    <PrivateRoute>
                      <RoleProtectedRoute allowedRoles={["superAdmin"]}>
                        <Demo />
                      </RoleProtectedRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/superdashboard"
                  element={
                    <PrivateRoute>
                      <RoleProtectedRoute allowedRoles={["superAdmin"]}>
                        <SuperDashboard />
                      </RoleProtectedRoute>
                    </PrivateRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </MainContent>
          </div>
          <PostItsOverlay ref={postItsRef} onOpenChange={setIsPostItsOpen} />
          <PinnedPostIts />
          <Modal ref={chatModalRef} />
        </>
      )}
    </div>
  );
}

export default App;
